import { useState } from "react";
import { ReportButton } from "@/components/moderation/ReportButton";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layers, Globe } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { JudgeBadge, RoleBadge } from "@/components/common/Badges";
import { CardGrid, CardGridSkeleton } from "@/components/cards/CardGrid";
import { EmptyState, Pager } from "@/components/common/PageParts";
import { PageLoader } from "@/components/common/PageLoader";
import { SetCard } from "@/pages/Sets";
import { CardBackTile } from "@/pages/CardBacks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CompetitiveHistory } from "@/components/competition/CompetitiveHistory";

function ProfileCards({ username }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({ queryKey: ["cards", "creator", username, page], queryFn: () => api.get("/cards", { params: { creator: username, page, page_size: 30 } }).then((r) => r.data) });
  if (isLoading) return <CardGridSkeleton />;
  if (!data.items.length) return <EmptyState icon={Layers} title="No public cards yet" testid="profile-cards-empty" />;
  return <><CardGrid cards={data.items} showOwner={false} testid="profile-cards-grid" /><Pager page={page} pageSize={30} total={data.total} onChange={setPage} testid="profile-cards-pager" /></>;
}

function ProfileList({ username, kind }) {
  const { data } = useQuery({ queryKey: [kind, "creator", username], queryFn: () => api.get(`/${kind}`, { params: { creator: username, page_size: 100 } }).then((r) => r.data.items) });
  if (!data) return null;
  if (!data.length) return <EmptyState title={`No public ${kind === "sets" ? "sets" : "card backs"} yet`} testid={`profile-${kind}-empty`} />;
  return kind === "sets" ? (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{data.map((s) => <SetCard key={s.set_id} set={s} showOwner={false} />)}</div>
  ) : (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">{data.map((b) => <CardBackTile key={b.card_back_id} back={{ ...b, owner: null }} />)}</div>
  );
}

export default function Profile() {
  const { username } = useParams();
  const { data: profile, isLoading, isError } = useQuery({ queryKey: ["profile", username], queryFn: () => api.get(`/users/${username}`).then((r) => r.data), retry: false });
  if (isLoading) return <PageLoader />;
  if (isError) return <div className="container-x py-24"><EmptyState title="Creator not found" testid="profile-not-found" /></div>;
  const joined = new Date(profile.created_at).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="container-x py-12" data-testid="profile-page">
      <header className="mb-12 flex flex-col gap-8 border-b border-amber-900/30 pb-10 md:flex-row md:items-end">
        {profile.avatar_url ? (
          <img src={assetUrl(profile.avatar_url)} alt="" className="h-28 w-28 rounded-2xl border border-amber-700/50 object-cover shadow-[0_0_24px_rgba(217,119,6,0.2)]" data-testid="profile-avatar" />
        ) : (
          <div className="flex h-28 w-28 items-center justify-center rounded-2xl border border-amber-700/50 bg-amber-900/20 font-display text-4xl text-amber-200">{profile.display_name[0]}</div>
        )}
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-3xl font-bold text-amber-100 sm:text-4xl" data-testid="profile-display-name">{profile.display_name}</h1>
            <RoleBadge role={profile.role} />
            <JudgeBadge level={profile.judge_level} />
            <ReportButton targetType="user" targetId={profile.user_id} ownerId={profile.user_id} label="Report profile" />
          </div>
          {profile.moderation_notice && <p className="mt-2 text-xs text-amber-300" data-testid="profile-moderation-notice">{profile.moderation_notice}</p>}
          <p className="mt-1 font-mono text-sm text-slate-500">@{profile.username} · joined {joined}</p>
          {profile.bio && <p className="mt-4 max-w-2xl whitespace-pre-line text-sm text-slate-300" data-testid="profile-bio">{profile.bio}</p>}
          {profile.website_url && (
            <a href={profile.website_url} target="_blank" rel="noreferrer nofollow" className="mt-3 inline-flex items-center gap-1.5 text-sm text-amber-300 hover:underline" data-testid="profile-website-link">
              <Globe className="h-4 w-4" />{profile.website_url.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
        <dl className="flex gap-8" data-testid="profile-stats">
          {[["Cards", profile.stats.cards], ["Sets", profile.stats.sets], ["Backs", profile.stats.card_backs]].map(([l, v]) => (
            <div key={l}><dt className="eyebrow text-[10px]">{l}</dt><dd className="font-heading text-2xl text-amber-100">{v}</dd></div>
          ))}
        </dl>
      </header>
      <Tabs defaultValue="cards">
        <TabsList className="mb-8 bg-[#121620]">
          <TabsTrigger value="cards" data-testid="profile-tab-cards">Cards</TabsTrigger>
          <TabsTrigger value="sets" data-testid="profile-tab-sets">Sets</TabsTrigger>
          <TabsTrigger value="backs" data-testid="profile-tab-backs">Card backs</TabsTrigger>
          <TabsTrigger value="competitive" data-testid="profile-tab-competitive">Competitive</TabsTrigger>
        </TabsList>
        <TabsContent value="cards"><ProfileCards username={profile.username} /></TabsContent>
        <TabsContent value="sets"><ProfileList username={profile.username} kind="sets" /></TabsContent>
        <TabsContent value="backs"><ProfileList username={profile.username} kind="card-backs" /></TabsContent>
        <TabsContent value="competitive"><CompetitiveHistory username={profile.username} /></TabsContent>
      </Tabs>
    </div>
  );
}
